/** The colour scales that have a tint: a light fill, a dark text and a border, per colour scheme. */
export const TINT_SCALES = ['brand', 'accent', 'success', 'warning', 'danger'] as const;
export type TintScale = (typeof TINT_SCALES)[number];

export type SurfaceTone = 'none' | 'surface' | 'page' | 'subtle' | 'filled' | 'creative' | 'scrim' | 'glass' | 'workspace' | TintScale;

/** The drop shadow steps of a raised block, from none to high. */
export type Elevation = 'none' | 'sm' | 'md' | 'lg' | 'xl';

/** The looks of the kit's own themed components. A kit component passes its look as `ownerState`,
 *  and the theme matches it with `variants`. */
export interface SurfaceLook {
  tone: SurfaceTone;
  outline: 'none' | 'solid' | 'dashed';
  radius: 'none' | 'sm' | 'md' | 'lg' | 'full';
  interactive: 'none' | 'brand' | 'quiet';
  selected: boolean;
  /** The surface is a control (a button, link or label), so it gets the pointer. */
  clickable: boolean;
  elevation: Elevation;
  dimmed: 'none' | 'some' | 'half';
}

export interface FieldLook {
  textSize: 'sm' | 'md' | 'strong' | 'lg' | 'title';
  quiet: boolean;
  prompting: boolean;
  mono: boolean;
  uppercase: boolean;
  highlighted: boolean;
}

export type TextTone =
  | 'inherit'
  | 'default'
  | 'heading'
  | 'muted'
  | 'disabled'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'onMedia';

export interface TextLook {
  mono: boolean;
  tone: TextTone;
  faded: boolean;
}

export interface CardLook {
  glow: boolean;
  lift: 'none' | 'md' | 'lg';
}

export interface EmptyStateLook {
  plain: boolean;
}

export interface AvatarLook {
  size: 'sm' | 'md' | 'lg' | 'xl';
  filled: boolean;
}

export interface BadgeLook {
  sentenceCase: boolean;
}

export interface IconButtonLook {
  danger: 'none' | 'quiet' | 'overlay';
  raised: boolean;
}

export interface MenuItemLook {
  destructive: boolean;
}

export interface TableRowLook {
  subtle: boolean;
}

export interface TableCellLook {
  sticky: boolean;
}

export type StepState = 'locked' | 'pending' | 'complete' | 'skipped';

export interface StepMarkLook {
  state: StepState;
  active: boolean;
}

export type CheckState = 'pass' | 'warn' | 'fail';

export interface CheckMarkLook {
  state: CheckState;
}

export interface CalendarDayLook {
  outside: boolean;
  between: boolean;
  edge: boolean;
}

export interface DropzoneLook {
  dragging: boolean;
}

export interface NavItemLook {
  rail: boolean;
  onSurface: boolean;
}

export interface FieldTriggerLook {
  muted: boolean;
}

export interface LogoLook {
  mark: boolean;
}

export type EditorMarkPart =
  | 'marquee'
  | 'frame'
  | 'handle'
  | 'guide'
  | 'safe-band'
  | 'safe-outline'
  | 'playhead'
  | 'playhead-head'
  | 'bar'
  | 'keyframe'
  | 'keyframe-start'
  | 'underline';

export interface EditorMarkLook {
  part: EditorMarkPart;
  selected: boolean;
}
